import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { Directive, ElementRef, EventEmitter, inject, Input, OnDestroy, Output, TemplateRef, ViewContainerRef } from '@angular/core';
import { take } from 'rxjs';
import { RmaOverlayService } from './overlay.service';
import { attachedConfig, bottomConfig, centerConfig, dropdownMenuConfig, fullscreenConfig, popoverConfig } from './overlay-config';

type RmaOverlayConfig = 'fullscreen' | 'bottom' | 'attached' | 'center' | 'dropdownMenu' | 'popover' | OverlayConfig;

type RmaOverlayScrollStrategy = 'close' | 'block';

@Directive({
  selector: '[rmaOverlay]',
  host: {
    '(click)': 'onClick()',
  },
  standalone: true,
})
export class OpenOverlayDirective implements OnDestroy {
  @Input({ required: true })
  public templateRef!: TemplateRef<unknown>;

  @Input()
  config: RmaOverlayConfig = 'attached';

  // The two converged copies disagreed on the anchored preset's scroll strategy, so callers
  // that need the other one say so here rather than the preset picking a winner for everyone.
  @Input()
  public scrollStrategy?: RmaOverlayScrollStrategy;

  @Output()
  public overlayOpen = new EventEmitter<OverlayRef>();

  @Output()
  public overlayClosed = new EventEmitter<OverlayRef>();

  private readonly vcr = inject(ViewContainerRef);
  private readonly elementRef = inject(ElementRef);
  private readonly overlay = inject(Overlay);
  private readonly overlayService = inject(RmaOverlayService);

  private overlayRef?: OverlayRef;

  public onClick(): void {
    const { overlayRef } = this.overlayService.open(this.vcr, this.templateRef, this.getConfig(this.config));

    this.overlayRef = overlayRef;

    overlayRef
      .backdropClick()
      .pipe(take(1))
      .subscribe(() => {
        overlayRef.detach();
        this.overlayClosed.emit();
      });

    this.overlayOpen.emit(overlayRef);
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
  }

  private getConfig(config: RmaOverlayConfig): OverlayConfig {
    const resolved = this.resolvePreset(config);

    if (!this.scrollStrategy) {
      return resolved;
    }

    return { ...resolved, scrollStrategy: this.overlay.scrollStrategies[this.scrollStrategy]() };
  }

  private resolvePreset(config: RmaOverlayConfig): OverlayConfig {
    if (config instanceof OverlayConfig) {
      return config;
    }

    switch (config) {
      case 'fullscreen':
        return fullscreenConfig(this.overlay);
      case 'bottom':
        return bottomConfig(this.overlay);
      case 'popover':
        return popoverConfig(this.overlay, this.elementRef);
      case 'center':
        return centerConfig(this.overlay);
      case 'dropdownMenu':
        return dropdownMenuConfig(this.overlay, this.elementRef);
      case 'attached':
      default:
        return attachedConfig(this.overlay, this.elementRef);
    }
  }
}
